/**
 * crud.ts - Tournament CRUD Operations
 *
 * This module handles tournament create, read, update, delete operations including:
 * - Creating new tournaments
 * - Editing existing tournaments
 * - Deleting tournaments
 * - Managing tournament modal UI
 */

import type { TournamentFormat } from '../../types/api';
import { TOURNAMENT_FORMATS } from '../utils/constants';
import { t } from '../utils/i18n-helper';
import { escapeHtml } from '../utils/security';
import { showConfirmDialog } from '../utils/confirm-dialog';
import {
  createTournament as createTournamentAPI,
  updateTournament as updateTournamentAPI,
  deleteTournamentAPI,
  fetchTournamentById
} from '../api/tournament-api';

/**
 * Tournament form data
 */
interface TournamentFormData {
  name: string;
  description: string;
  venue?: string;
  logo?: string;
  format: TournamentFormat;
  startDate: string;
  endDate: string;
  numberOfRounds?: number;
  initialColor?: 'white' | 'black' | 'random';
  location?: string;
  timeControl?: string;
  isRated?: boolean;
  allowSelfRegistration?: boolean;
}

/**
 * Helper function to update conditional field visibility based on format
 * @param format - Tournament format
 */
export function updateFormatConditionalFields(format: string): void {
  const roundsGroup = document.getElementById('number-of-rounds-group');
  const initialColorGroup = document.getElementById('initial-color-group');

  // Number of rounds is only for Swiss
  if (format === TOURNAMENT_FORMATS.SWISS) {
    if (roundsGroup) roundsGroup.style.display = 'block';
  } else {
    if (roundsGroup) roundsGroup.style.display = 'none';
  }

  // Initial color is for Swiss and Round Robin, but not Knockout
  if (format === TOURNAMENT_FORMATS.SWISS || format === TOURNAMENT_FORMATS.ROUND_ROBIN) {
    if (initialColorGroup) initialColorGroup.style.display = 'block';
  } else {
    if (initialColorGroup) initialColorGroup.style.display = 'none';
  }
}

/**
 * Initialize location autocomplete for venue field
 */
let venueAutocompleteCleanup: (() => void) | null = null;

function initializeVenueAutocomplete(): void {
  // Clean up previous autocomplete if exists
  if (venueAutocompleteCleanup) {
    venueAutocompleteCleanup();
    venueAutocompleteCleanup = null;
  }

  const venueInput = document.getElementById('tournament-venue') as HTMLInputElement | null;
  const suggestionsDiv = document.getElementById('venue-suggestions') as HTMLElement | null;

  if (!venueInput || !suggestionsDiv) return;

  let debounceTimer: number | undefined;

  const searchLocation = async (query: string) => {
    if (query.length < 2) {
      suggestionsDiv.style.display = 'none';
      return;
    }

    try {
      const response = await fetch(`/api/tournaments/location-search?q=${encodeURIComponent(query)}&limit=5`);
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        suggestionsDiv.innerHTML = data.results.map((result: any) => `
          <div class="location-suggestion-item" data-location="${escapeHtml(result.shortDisplayName || result.displayName)}">
            ${escapeHtml(result.shortDisplayName || result.displayName)}
          </div>
        `).join('');
        suggestionsDiv.style.display = 'block';

        // Add click handlers to suggestions
        suggestionsDiv.querySelectorAll('.location-suggestion-item').forEach(item => {
          item.addEventListener('click', () => {
            const location = (item as HTMLElement).getAttribute('data-location');
            if (location) {
              venueInput.value = location;
              suggestionsDiv.style.display = 'none';
            }
          });
        });
      } else {
        suggestionsDiv.style.display = 'none';
      }
    } catch (error) {
      console.error('Location search error:', error);
      suggestionsDiv.style.display = 'none';
    }
  };

  const handleInput = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      searchLocation(venueInput.value);
    }, 300);
  };

  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    if (!venueInput.contains(target) && !suggestionsDiv.contains(target)) {
      suggestionsDiv.style.display = 'none';
    }
  };

  venueInput.addEventListener('input', handleInput);
  document.addEventListener('click', handleClickOutside);

  // Store cleanup function
  venueAutocompleteCleanup = () => {
    venueInput.removeEventListener('input', handleInput);
    document.removeEventListener('click', handleClickOutside);
    if (debounceTimer) clearTimeout(debounceTimer);
  };
}

/**
 * Handle logo file selection
 */
export function handleLogoSelect(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (!file) return;

  // Validate file size (max 2MB)
  if (file.size > 2 * 1024 * 1024) {
    alert(t('tournament.logoTooLarge', 'Logo file is too large. Maximum size is 2MB.'));
    input.value = '';
    return;
  }

  // Read file as base64
  const reader = new FileReader();
  reader.onload = (e) => {
    const base64 = e.target?.result as string;
    const logoUrlInput = document.getElementById('tournament-logo-url') as HTMLInputElement;
    if (logoUrlInput) {
      logoUrlInput.value = base64;
    }
    updateLogoPreview(base64);
  };
  reader.readAsDataURL(file);
}

/**
 * Update logo preview
 */
function updateLogoPreview(logoUrl: string): void {
  const preview = document.getElementById('logo-preview');
  const previewImg = document.getElementById('logo-preview-img') as HTMLImageElement;

  if (logoUrl && preview && previewImg) {
    previewImg.src = logoUrl;
    preview.style.display = 'block';
  }
}

/**
 * Clear logo
 */
export function clearLogo(): void {
  const logoUrlInput = document.getElementById('tournament-logo-url') as HTMLInputElement;
  const logoFileInput = document.getElementById('tournament-logo-file') as HTMLInputElement;
  const preview = document.getElementById('logo-preview');

  if (logoUrlInput) logoUrlInput.value = '';
  if (logoFileInput) logoFileInput.value = '';
  if (preview) preview.style.display = 'none';
}

/**
 * Remove any Leaflet elements from the modal
 */
function removeLeafletElements(): void {
  const modal = document.getElementById('tournament-modal');
  if (!modal) return;

  // Remove all elements with "leaflet" in their class name
  const leafletElements = modal.querySelectorAll('[class*="leaflet"]');
  leafletElements.forEach(el => el.remove());

  // Set up a mutation observer to remove Leaflet elements if they appear
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          // Check if the node itself or any of its children have leaflet classes
          if (node.className && typeof node.className === 'string' && node.className.includes('leaflet')) {
            node.remove();
          }
          const leafletChildren = node.querySelectorAll('[class*="leaflet"]');
          leafletChildren.forEach(el => el.remove());
        }
      });
    });
  });

  // Start observing
  observer.observe(modal, {
    childList: true,
    subtree: true
  });

  // Store observer so we can disconnect it later
  (modal as any)._leafletObserver = observer;
}

/**
 * Show the create tournament modal
 */
export function showCreateTournament(): void {
  const modal = document.getElementById('tournament-modal');
  const title = document.getElementById('tournament-modal-title');
  const submitBtn = document.getElementById('tournament-submit-btn');
  const form = document.getElementById('tournament-form') as HTMLFormElement | null;
  const tournamentId = document.getElementById('tournament-id') as HTMLInputElement | null;
  const tournamentOriginalFormat = document.getElementById('tournament-original-format') as HTMLInputElement | null;
  const startDate = document.getElementById('start-date') as HTMLInputElement | null;
  const endDate = document.getElementById('end-date') as HTMLInputElement | null;
  const tournamentError = document.getElementById('tournament-error');
  const tournamentWarning = document.getElementById('tournament-warning');

  if (!modal || !title || !submitBtn || !form) return;

  title.textContent = t('tournament.createNew', 'Create New Tournament');
  submitBtn.textContent = t('tournament.create', 'Create Tournament');

  // Clear the form
  form.reset();
  if (tournamentId) tournamentId.value = '';
  if (tournamentOriginalFormat) tournamentOriginalFormat.value = '';

  // Set default dates to today
  const today = new Date().toISOString().split('T')[0]!;
  if (startDate) startDate.value = today;
  if (endDate) endDate.value = today;

  // Show Swiss conditional groups by default (since Swiss is the default format)
  // IMPORTANT: Do this BEFORE showing the modal to avoid resize flicker
  updateFormatConditionalFields(TOURNAMENT_FORMATS.SWISS);

  // Clear error messages
  if (tournamentError) tournamentError.textContent = '';
  if (tournamentWarning) tournamentWarning.textContent = '';

  // Remove any Leaflet elements that might interfere
  removeLeafletElements();

  // Initialize venue autocomplete
  initializeVenueAutocomplete();

  // Wait for next frame to ensure all layout calculations are complete before showing modal
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.style.display = 'block';
    });
  });
}

/**
 * Close the tournament modal
 */
export function closeTournamentModal(): void {
  const modal = document.getElementById('tournament-modal');
  const form = document.getElementById('tournament-form') as HTMLFormElement | null;
  const tournamentError = document.getElementById('tournament-error');
  const tournamentWarning = document.getElementById('tournament-warning');

  if (modal) {
    modal.style.display = 'none';

    // Disconnect the Leaflet observer if it exists
    if ((modal as any)._leafletObserver) {
      (modal as any)._leafletObserver.disconnect();
      (modal as any)._leafletObserver = null;
    }
  }

  // Clear the form
  if (form) {
    form.reset();
  }
  if (tournamentError) tournamentError.textContent = '';
  if (tournamentWarning) tournamentWarning.textContent = '';

  // Clean up venue autocomplete
  if (venueAutocompleteCleanup) {
    venueAutocompleteCleanup();
    venueAutocompleteCleanup = null;
  }
}

/**
 * Keep old function names for backward compatibility
 */
export function hideCreateTournament(): void {
  closeTournamentModal();
}

/**
 * Keep old function for backward compatibility
 */
export function closeEditTournamentModal(): void {
  closeTournamentModal();
}

/**
 * Handle tournament create/update form submission
 * @param e - Form submit event
 */
export async function handleCreateTournament(e: Event): Promise<void> {
  e.preventDefault();

  const tournamentId = (document.getElementById('tournament-id') as HTMLInputElement | null)?.value || '';
  const format = ((document.getElementById('tournament-format') as HTMLSelectElement | null)?.value || '') as TournamentFormat;
  const venueValue = (document.getElementById('tournament-venue') as HTMLInputElement | null)?.value.trim() || '';
  const name = (document.getElementById('tournament-name') as HTMLInputElement | null)?.value || '';
  const timeControlValue = (document.getElementById('tournament-time-control') as HTMLInputElement | null)?.value.trim() || '';
  const startDateValue = (document.getElementById('start-date') as HTMLInputElement | null)?.value || '';
  const endDateValue = (document.getElementById('end-date') as HTMLInputElement | null)?.value || '';

  const tournamentData: TournamentFormData = {
    name,
    venue: venueValue || undefined,
    timeControl: timeControlValue || undefined,
    format,
    startDate: startDateValue || new Date().toISOString(),
    endDate: endDateValue || ''
  };

  // Add numberOfRounds and initialColor for Swiss tournaments
  if (format === TOURNAMENT_FORMATS.SWISS) {
    const numberOfRoundsInput = document.getElementById('number-of-rounds') as HTMLInputElement | null;
    const numberOfRounds = numberOfRoundsInput ? parseInt(numberOfRoundsInput.value) : 0;
    if (!numberOfRounds || numberOfRounds < 1) {
      alert(t('tournament.validRounds', 'Please enter a valid number of rounds (minimum 1)'));
      return;
    }
    tournamentData.numberOfRounds = numberOfRounds;

    // Add initial color for Swiss
    const initialColorInput = document.getElementById('initial-color') as HTMLSelectElement | null;
    const initialColor = initialColorInput?.value as 'white' | 'black' | 'random' | undefined;
    if (initialColor) {
      tournamentData.initialColor = initialColor;
    }
  }

  try {
    // Check if user is logged in
    const currentUser = window.getCurrentUser ? window.getCurrentUser() : null;

    if (tournamentId) {
      // Edit existing tournament
      await updateTournamentAPI(tournamentId, tournamentData);
    } else {
      // Create new tournament
      // If user is not logged in, mark as demo tournament
      const createData = currentUser
        ? tournamentData
        : { ...tournamentData, isDemo: true };

      const newTournament = await createTournamentAPI(createData);

      // If demo tournament, store ID in sessionStorage
      if (!currentUser && newTournament?.id) {
        const demoTournaments = JSON.parse(sessionStorage.getItem('demoTournaments') || '[]');
        if (!demoTournaments.includes(newTournament.id)) {
          demoTournaments.push(newTournament.id);
          sessionStorage.setItem('demoTournaments', JSON.stringify(demoTournaments));
        }
      }
    }

    const form = e.target as HTMLFormElement;
    form.reset();
    closeTournamentModal();

    // Reload tournaments - for logged in users use loadTournamentsList, for guests reload page
    if (currentUser) {
      if (window.loadTournamentsList) {
        window.loadTournamentsList();
      }
    } else {
      // For guest users, reload page to trigger showGuestWelcomeMessage with updated sessionStorage
      window.location.reload();
    }
  } catch (error) {
    console.error('Error saving tournament:', error);
    const tournamentError = document.getElementById('tournament-error');
    if (tournamentError) {
      tournamentError.textContent = `${t('common.error', 'Error')}: ${(error as Error).message}`;
    }
  }
}

/**
 * Show the edit tournament modal with pre-filled data
 * @param id - Tournament ID
 */
export async function editTournament(id: string): Promise<void> {
  try {
    // Fetch current tournament data
    const tournament = await fetchTournamentById(id);

    const modal = document.getElementById('tournament-modal');
    const title = document.getElementById('tournament-modal-title');
    const submitBtn = document.getElementById('tournament-submit-btn');

    if (!modal || !title || !submitBtn) return;

    title.textContent = t('tournament.edit', 'Edit Tournament');
    submitBtn.textContent = t('tournament.saveChanges', 'Save Changes');

    // Populate the form
    const tournamentIdInput = document.getElementById('tournament-id') as HTMLInputElement | null;
    const tournamentOriginalFormat = document.getElementById('tournament-original-format') as HTMLInputElement | null;
    const tournamentName = document.getElementById('tournament-name') as HTMLInputElement | null;
    const tournamentVenue = document.getElementById('tournament-venue') as HTMLInputElement | null;
    const tournamentTimeControl = document.getElementById('tournament-time-control') as HTMLInputElement | null;
    const tournamentFormat = document.getElementById('tournament-format') as HTMLSelectElement | null;
    const startDateInput = document.getElementById('start-date') as HTMLInputElement | null;
    const endDateInput = document.getElementById('end-date') as HTMLInputElement | null;
    const numberOfRoundsInput = document.getElementById('number-of-rounds') as HTMLInputElement | null;

    if (tournamentIdInput) tournamentIdInput.value = tournament.id;
    if (tournamentOriginalFormat) tournamentOriginalFormat.value = tournament.format;
    if (tournamentName) tournamentName.value = tournament.name;
    if (tournamentVenue) tournamentVenue.value = tournament.venue || '';
    if (tournamentTimeControl) tournamentTimeControl.value = tournament.timeControl || '';
    if (tournamentFormat) tournamentFormat.value = tournament.format;

    // Handle date fields
    if (tournament.startDate && startDateInput) {
      const startDate = new Date(tournament.startDate);
      startDateInput.value = startDate.toISOString().split('T')[0]!;
    }
    if (tournament.endDate && endDateInput) {
      const endDate = new Date(tournament.endDate);
      endDateInput.value = endDate.toISOString().split('T')[0]!;
    }

    // Handle number of rounds for Swiss
    if (tournament.format === TOURNAMENT_FORMATS.SWISS && numberOfRoundsInput) {
      numberOfRoundsInput.value = String(tournament.numberOfRounds || 5);
    }

    // Handle initial color
    const initialColorInput = document.getElementById('initial-color') as HTMLSelectElement | null;
    if (initialColorInput && tournament.initialColor) {
      initialColorInput.value = tournament.initialColor;
    }

    // Update conditional fields visibility
    updateFormatConditionalFields(tournament.format);

    // Clear any previous messages
    const tournamentError = document.getElementById('tournament-error');
    const tournamentWarning = document.getElementById('tournament-warning');
    if (tournamentError) tournamentError.textContent = '';
    if (tournamentWarning) tournamentWarning.textContent = '';

    // Show the modal
    modal.style.display = 'block';

    // Remove any Leaflet elements that might interfere
    removeLeafletElements();

    // Initialize venue autocomplete
    initializeVenueAutocomplete();
  } catch (error) {
    console.error('Error loading tournament for edit:', error);
    alert(t('tournament.loadError', 'Failed to load tournament data'));
  }
}

/**
 * Delete a tournament with confirmation
 * @param id - Tournament ID
 */
export async function deleteTournament(id: string): Promise<void> {
  const confirmed = await showConfirmDialog(
    t('tournament.confirmDelete', 'Are you sure you want to delete this tournament?')
  );

  if (!confirmed) {
    return;
  }

  try {
    await deleteTournamentAPI(id);

    // Reload tournaments list
    if (window.loadTournamentsList) {
      window.loadTournamentsList();
    }
  } catch (error) {
    console.error('Error deleting tournament:', error);
    alert(t('tournament.deleteError', 'Error deleting tournament'));
  }
}

// Export to window for onclick handlers
window.updateFormatConditionalFields = updateFormatConditionalFields;
window.showCreateTournament = showCreateTournament;
window.closeTournamentModal = closeTournamentModal;
window.hideCreateTournament = hideCreateTournament;
window.closeEditTournamentModal = closeEditTournamentModal;
window.handleCreateTournament = handleCreateTournament;
window.editTournament = editTournament;
window.deleteTournament = deleteTournament;
window.handleLogoSelect = handleLogoSelect;
window.clearLogo = clearLogo;

// Set up logo URL input listener on page load
document.addEventListener('DOMContentLoaded', () => {
  const logoUrlInput = document.getElementById('tournament-logo-url') as HTMLInputElement;
  if (logoUrlInput) {
    logoUrlInput.addEventListener('input', () => {
      const url = logoUrlInput.value.trim();
      if (url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:image/'))) {
        updateLogoPreview(url);
      }
    });
  }
});
